import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Modal from '../common/Modal';
import { formatPhoneNumber } from '../../utils/format';
import { ConsoleLabel as MainLabel, ConsoleSelect as MainSelect, ConsoleInput as MainInput, FieldError } from '../../styles/common/input.styled';
import { Button } from '../../styles/common/Button.styled';
import toast from 'react-hot-toast';
import { useApi } from '../../hooks/useApi';

interface PhoneInfo {
  id: number;
  phone: string;
  displayName: string;
}

interface PhoneRoutingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (updatedData: any) => void;
  initialData?: any;
}

interface IvrOption {
  id: number;
  name: string;
}

interface RingingGroupOption {
  id: number;
  displayName: string;
}

const FormGroup = styled.div`display: flex; flex-direction: column; gap: 6px; margin-bottom: 16px;`;





const InfoBox = styled.div`padding: 0 0 16px; margin-bottom: 20px; border-bottom: 1px solid ${({ theme }) => theme.color.primary_300};`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  &:last-child {
    margin-bottom: 0;
  }
`;

const InfoLabel = styled.span`
  color: ${({ theme }) => theme.color.text_color_300};
  font-size: 14px;
`;

const InfoValue = styled.span`
  color: ${({ theme }) => theme.color.text_color_100};
  font-weight: 600;
  font-size: 14px;
`;

const PhoneRoutingModal: React.FC<PhoneRoutingModalProps> = ({ isOpen, onClose, onSuccess, initialData }) => {
  const { data: phones, request: fetchPhones } = useApi<PhoneInfo[]>();
  const { data: ivrData, request: fetchIvrs } = useApi<any>();
  const { data: queueData, request: fetchQueues } = useApi<any>();

  const [fieldError, setFieldError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    routingMode: 'IVR',
    ivrInfoId: undefined as number | undefined,
    ringingGroupId: '',
    phoneId: ''
  });

  const ivrs = (ivrData?.content || (Array.isArray(ivrData) ? ivrData : [])) as IvrOption[];
  const ringingGroups = (queueData?.content || (Array.isArray(queueData) ? queueData : [])) as RingingGroupOption[];

  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name || '',
        routingMode: initialData.routingMode || 'IVR',
        ivrInfoId: initialData.ivrInfoId,
        ringingGroupId: initialData.ringingGroupId || '',
        phoneId: initialData.phoneId || initialData.phone_id || ''
      });
    }

    if (isOpen) {
      fetchPhones({ url: '/user/getPhones', method: 'GET', params: { isSettable: true } }).catch(err => {
        console.error('Failed to fetch phones:', err);
      });
      fetchIvrs({ url: '/ivr', method: 'GET', params: { limit: 100, pg: 1 } }).catch(err => {
        console.error('Failed to fetch IVRs:', err);
      });
      fetchQueues({ url: '/ringing-group', method: 'GET', params: { limit: 100, pg: 1 } }).catch(err => {
        console.error('Failed to fetch ringing groups:', err);
      });
    }
  }, [initialData, isOpen, fetchPhones, fetchIvrs, fetchQueues]);

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
    const { name, value } = e.target;
    setFieldError('');
    setFormData(prev => ({
        ...prev,
        [name]: value,
        // 라우팅 모드가 바뀌면 값 초기화
        ...(name === 'routingMode' ? { ivrInfoId: undefined, ringingGroupId: '', phoneId: '' } : {})
    }));
  };

  const { loading: saving, request: updateMainPhone } = useApi<any>();

  const handleSubmit = () => {
    if (formData.routingMode === 'IVR' && !formData.ivrInfoId) {
      setFieldError('IVR을 선택해주세요.');
      toast.error('IVR을 선택해주세요.');
      return;
    }
    if (formData.routingMode === 'RINGING_GROUP' && !formData.ringingGroupId) {
      setFieldError('수신그룹을 선택해주세요.');
      toast.error('수신그룹을 선택해주세요.');
      return;
    }
    if (formData.routingMode === 'DIRECT' && !formData.phoneId) {
      setFieldError('직접연결을 선택해주세요.');
      toast.error('직접연결을 선택해주세요.');
      return;
    }

    const payload = {
      name: formData.name,
      routingMode: formData.routingMode,
      ivrInfoId: formData.routingMode === 'IVR' ? Number(formData.ivrInfoId) : null,
      ringingGroupId: formData.routingMode === 'RINGING_GROUP' ? Number(formData.ringingGroupId) : null,
      phoneId: formData.routingMode === 'DIRECT' ? Number(formData.phoneId) : null,
    };

    updateMainPhone({
      url: `/main-phone/${initialData.id}`,
      method: 'PUT',
      data: payload
    }).then(() => {
      toast.success('라우팅 설정이 성공적으로 수정되었습니다.');
      onSuccess(null);
      onClose();
    }).catch(err => {
      console.error('Failed to update main phone:', err);
      toast.error('수정에 실패했습니다.');
    });
  };

  if (!isOpen) return null;

  const getRoutingValueLabel = () => {
    switch (formData.routingMode) {
      case 'IVR': return 'IVR 명칭';
      case 'RINGING_GROUP': return '수신그룹 명칭';
      case 'DIRECT': return '직접연결 명칭';
      default: return '연결 대상';
    }
  };

  return (
    <Modal
      title="전화번호 라우팅 설정 수정"
      onClose={onClose}
      width={520} footer={<><Button $variant="secondary" onClick={onClose}>취소</Button><Button onClick={handleSubmit} disabled={saving}>{saving ? '저장 중...' : '저장하기'}</Button></>}
    >
      {initialData && (
        <InfoBox>
          <InfoRow>
            <InfoLabel>전화번호</InfoLabel>
            <InfoValue>{formatPhoneNumber(initialData.phone)}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>대표번호</InfoLabel>
            <InfoValue>{initialData.generalPhone ? formatPhoneNumber(initialData.generalPhone) : '-'}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>연결 대상</InfoLabel>
            <InfoValue>{initialData.routingRelatedName || '-'}</InfoValue>
          </InfoRow>
        </InfoBox>
      )}

      <FormGroup>
        <MainLabel htmlFor="route-name">명칭</MainLabel>
        <MainInput
          id="route-name" name="name"
          value={formData.name}
          onChange={handleChange}
          placeholder="명칭을 입력하세요"
        />
      </FormGroup>

      <FormGroup>
        <MainLabel htmlFor="route-mode">라우팅 모드 *</MainLabel>
        <MainSelect
          id="route-mode" name="routingMode"
          value={formData.routingMode}
          onChange={handleChange}
        >
          <option value="IVR">IVR</option>
          <option value="RINGING_GROUP">수신그룹</option>
          <option value="DIRECT">직접연결</option>
        </MainSelect>
      </FormGroup>

      <FormGroup>
        <MainLabel htmlFor="route-target">{getRoutingValueLabel()} *</MainLabel>
        {formData.routingMode === 'IVR' ? (
            <MainSelect aria-invalid={Boolean(fieldError)} aria-describedby={fieldError ? "route-target-error" : undefined} id="route-target" name="ivrInfoId" value={formData.ivrInfoId || ''} onChange={handleChange}>
                <option value="">IVR 선택</option>
                {ivrs.map(ivr => (
                    <option key={ivr.id} value={ivr.id}>
                        {ivr.name}
                    </option>
                ))}
            </MainSelect>
        ) : formData.routingMode === 'RINGING_GROUP' ? (
            <MainSelect aria-invalid={Boolean(fieldError)} aria-describedby={fieldError ? "route-target-error" : undefined} id="route-target" name="ringingGroupId" value={formData.ringingGroupId} onChange={handleChange}>
                <option value="">수신그룹 선택</option>
                {ringingGroups.map(ringingGroup => (
                    <option key={ringingGroup.id} value={ringingGroup.id}>
                        {ringingGroup.displayName}
                    </option>
                ))}
            </MainSelect>
        ) : (
            <MainSelect aria-invalid={Boolean(fieldError)} aria-describedby={fieldError ? "route-target-error" : undefined} id="route-target" name="phoneId" value={formData.phoneId} onChange={handleChange}>
                <option value="">직접연결 선택</option>
                {phones?.map(phone => (
                    <option key={phone.id} value={phone.id}>
                        {phone.displayName}
                    </option>
                ))}
            </MainSelect>
        )}
        {fieldError && <FieldError id="route-target-error">{fieldError}</FieldError>}
      </FormGroup>


    </Modal>
  );
};

export default PhoneRoutingModal;
